/**
 * The site's title and description, used for the page title, the meta description, the link
 * preview (Open Graph) and the installed app. Set at build time with VITE_SITE_TITLE and
 * VITE_SITE_DESCRIPTION; vite.config.ts writes them into index.html, and the app reads them back
 * from there (readSiteMeta), so changing them needs only a new build, not a code change.
 * No `import.meta` here: vite.config.ts and jest both load this file.
 */
export const DEFAULT_SITE_TITLE = 'Vault Play';
export const DEFAULT_SITE_DESCRIPTION =
  'Vault Play: one wallet for all your favorite games. Add money, play and cash out from your phone.';

export interface SiteMeta {
  title: string;
  description: string;
}

/** The meta for a build: the env values when set, else the Vault Play defaults. */
export function resolveSiteMeta(env: { VITE_SITE_TITLE?: string; VITE_SITE_DESCRIPTION?: string }): SiteMeta {
  return {
    title: String(env.VITE_SITE_TITLE ?? '').trim() || DEFAULT_SITE_TITLE,
    description: String(env.VITE_SITE_DESCRIPTION ?? '').trim() || DEFAULT_SITE_DESCRIPTION,
  };
}

let fromPage: SiteMeta | null = null;

/**
 * The title and description the built index.html was served with. Read once, before any page
 * changes them (the partner page sets its own), so going back to another page restores them.
 */
export function readSiteMeta(doc: Document | undefined = typeof document === 'undefined' ? undefined : document): SiteMeta {
  if (fromPage) return fromPage;
  const title = doc?.title?.trim() || DEFAULT_SITE_TITLE;
  const description =
    doc?.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() || DEFAULT_SITE_DESCRIPTION;
  fromPage = { title, description };
  return fromPage;
}

/** Tests only: forget the cached values. */
export function resetSiteMetaForTests(): void {
  fromPage = null;
}
